// The game loop.
//
// One rAF, one accumulator, one fixed 60Hz tick. Glenn Fiedler's "Fix
// Your Timestep!" applied literally: real elapsed time goes into an
// accumulator, whole ticks come out, and the leftover fraction is handed
// to the renderer as `alpha` so motion stays smooth on a 144Hz panel
// without the simulation ever seeing a variable `dt`.
//
// This file exists because the old DAEMON had no loop at all: eight
// `setInterval`s, forty-odd `setTimeout`s and two rAF sites, each with
// its own idea of what time it was, and hit detection living inside
// timer callbacks. Two consequences the rebuild must not repeat:
//
//   1. **Death spirals.** Nothing clamped catch-up. Alt-tab for a minute
//      and a hundred seconds of bullets resolved in one blocking burst,
//      killing a player who wasn't at the keyboard. Here the accumulator
//      is capped at MAX_CATCHUP_TICKS: a stall drops the missing time on
//      the floor rather than replaying it at you. IGT (`state.tick`) is
//      the only clock the game reports, so dropping wall-clock time
//      costs nothing that anyone can observe.
//
//   2. **Leaks.** Timers outlived the window and dispatched damage into
//      a closed app. `stop()` here cancels the frame and removes every
//      listener it installed; after it returns, nothing in this module
//      can run again.
//
// The loop is deliberately host-agnostic and injectable: `now`, `raf`
// and `cancel` can be supplied, which is how the tests drive it with a
// fake clock and no browser.

import { step } from "./sim";
import { EMPTY_INPUT, TICK_MS, type SimEvent, type SimInput, type SimState } from "./types";

/**
 * Hard cap on ticks simulated per animation frame.
 *
 * Five ticks is 83ms of catch-up: enough to ride out a GC pause or a
 * layout thrash without visibly slowing down, far short of enough to
 * resolve a backgrounded tab's worth of bullets in one go. Raising this
 * makes long stalls "fairer" in the replay sense and much more lethal in
 * the sense that matters.
 */
export const MAX_CATCHUP_TICKS = 5;

/**
 * Slack on the "is there a whole tick in the accumulator?" test, in ms.
 *
 * TICK_MS is 1000/60, which is not representable in binary, so draining
 * n ticks' worth of accumulator by repeated subtraction lands a few
 * ulps short of zero and silently eats the nth tick. A microsecond of
 * tolerance is far below anything the game can observe and removes an
 * entire class of off-by-one.
 */
const TICK_EPSILON = 1e-6;

export type Loop = {
  start(): void;
  stop(): void;
  pause(p: boolean): void;
  getState(): SimState;
};

export type LoopOptions = {
  /** Starting state. The loop takes ownership from `start()` onward. */
  state: SimState;
  /** Called once per animation frame, after any ticks. `alpha` is the
   *  unconsumed fraction of a tick, 0-1, for render interpolation. */
  onFrame: (state: SimState, alpha: number) => void;
  /** Sampled once per **tick**, not per frame — so a frame that runs
   *  three ticks reads input three times and the replay stream stays
   *  one-input-per-tick. */
  readInput: () => SimInput;
  /** Drained every tick that produced events. The sim never plays a
   *  sound or shakes a screen; this is where that becomes someone
   *  else's problem.
   *
   *  The post-tick state comes with them, because a handler almost
   *  always needs both — a `lost` event whose stats are read off the
   *  *previous* tick is a results screen that under-counts the hit that
   *  killed you. */
  onEvents?: (events: SimEvent[], state: SimState) => void;
  /** Notified whenever the effective paused state flips, including the
   *  automatic pauses on tab-hide and window-blur. The shell uses this
   *  to put up its "click to resume" plate. */
  onPause?: (paused: boolean) => void;
  /** Set false to opt out of the visibility/focus auto-pause (tests). */
  autoPause?: boolean;
  /** Input the tick *before* the first one. The sim derives cursor
   *  velocity from it — the `lead` emitter aims where you are going —
   *  so a loop swapped in mid-run should pass the live cursor rather
   *  than let the first tick see a jump from the arena's centre. */
  prevInput?: SimInput;

  // ── Injectables, for tests and for headless replay ────────────────
  now?: () => number;
  raf?: (cb: (t: number) => void) => number;
  cancel?: (handle: number) => void;
};

export function createLoop(opts: LoopOptions): Loop {
  const now =
    opts.now ??
    (typeof performance !== "undefined" ? () => performance.now() : () => Date.now());
  const raf =
    opts.raf ??
    (typeof requestAnimationFrame === "function"
      ? (cb: (t: number) => void) => requestAnimationFrame(cb)
      : (cb: (t: number) => void) => setTimeout(() => cb(now()), TICK_MS) as unknown as number);
  const cancel =
    opts.cancel ??
    (typeof cancelAnimationFrame === "function"
      ? (h: number) => cancelAnimationFrame(h)
      : (h: number) => clearTimeout(h as unknown as ReturnType<typeof setTimeout>));

  const autoPause = opts.autoPause !== false;

  let state = opts.state;
  /** The sim needs the previous tick's input to derive cursor velocity
   *  (the `lead` emitter aims where you're going, not where you are). */
  let prevInput: SimInput = opts.prevInput ?? EMPTY_INPUT;

  let acc = 0;
  let last = 0;
  let handle: number | null = null;
  let running = false;
  let pausedByUser = false;
  let pausedByHost = false;
  let reportedPaused = false;

  const isPaused = (): boolean => pausedByUser || pausedByHost;

  /** Announce a pause transition exactly once per edge. */
  function syncPause(): void {
    const p = isPaused();
    if (p === reportedPaused) return;
    reportedPaused = p;
    // Resuming: the clock has been running the whole time we were not.
    // Rebase it, or the first live frame inherits the entire pause as
    // elapsed time and burns the whole catch-up budget on nothing.
    if (!p) {
      last = now();
      acc = 0;
    }
    opts.onPause?.(p);
  }

  function schedule(): void {
    if (!running || handle !== null) return;
    handle = raf(frame);
  }

  function frame(): void {
    handle = null;
    if (!running) return;

    const t = now();
    let elapsed = t - last;
    last = t;
    // Guard against a clock that goes backwards or hands us a NaN. Both
    // happen: `performance.now()` is monotonic in theory, and a fake or
    // remoted clock is not obliged to be.
    if (!Number.isFinite(elapsed) || elapsed < 0) elapsed = 0;

    if (!isPaused()) {
      acc += elapsed;

      // The clamp. Everything above the cap is discarded, not queued —
      // see the note at the top of the file.
      const cap = TICK_MS * MAX_CATCHUP_TICKS;
      if (acc > cap) acc = cap;

      while (acc >= TICK_MS - TICK_EPSILON) {
        acc -= TICK_MS;
        const input = opts.readInput();
        state = step(state, input, prevInput);
        prevInput = input;
        if (state.events.length > 0) opts.onEvents?.(state.events, state);
        // A tick can stop the loop from underneath us — a `lost` event
        // handler is entitled to call `stop()`. Bail rather than
        // stepping a run the shell has already torn down.
        if (!running) return;
      }
    }

    // `acc` can sit a few ulps below zero after the epsilon-tolerant
    // drain; alpha is a lerp weight and must stay in [0, 1).
    opts.onFrame(state, acc > 0 ? acc / TICK_MS : 0);
    schedule();
  }

  // ── Host pause: hidden tab or blurred window ──────────────────────
  //
  // rAF is throttled or stopped outright in a background tab, so without
  // this the loop would come back to a multi-second `elapsed` and eat
  // the clamp on every return. More importantly: a bullet hell you
  // cannot see is not a bullet hell you should be losing.

  const onVisibility = (): void => {
    pausedByHost = typeof document !== "undefined" && document.visibilityState === "hidden";
    syncPause();
    // Coming back from hidden, rAF was never rescheduled — restart it.
    schedule();
  };
  const onBlur = (): void => {
    pausedByHost = true;
    syncPause();
  };
  const onFocus = (): void => {
    pausedByHost = typeof document !== "undefined" && document.visibilityState === "hidden";
    syncPause();
    schedule();
  };

  let listening = false;
  function listen(): void {
    if (listening || !autoPause) return;
    if (typeof document === "undefined" || typeof window === "undefined") return;
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    listening = true;
  }
  function unlisten(): void {
    if (!listening) return;
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("blur", onBlur);
    window.removeEventListener("focus", onFocus);
    listening = false;
  }

  return {
    start(): void {
      if (running) return;
      running = true;
      last = now();
      acc = 0;
      // Don't inherit a stale pause reason from a previous run, but do
      // respect a tab that is genuinely hidden right now.
      pausedByHost =
        autoPause && typeof document !== "undefined" && document.visibilityState === "hidden";
      reportedPaused = isPaused();
      listen();
      schedule();
    },

    stop(): void {
      running = false;
      if (handle !== null) {
        cancel(handle);
        handle = null;
      }
      unlisten();
      acc = 0;
    },

    pause(p: boolean): void {
      pausedByUser = p;
      syncPause();
      // Unpausing after a stop should stay stopped; `schedule` checks.
      schedule();
    },

    getState(): SimState {
      return state;
    },
  };
}
